package ru.villawonka.app.presentation.design.components

import androidx.compose.foundation.background
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.Colors
import androidx.compose.material.ExperimentalMaterialApi
import androidx.compose.material.Icon
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.material.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.ui.tooling.preview.Preview
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.AppColors
import ru.villawonka.app.presentation.design.theme.AppTextStyles
import ru.villawonka.app.presentation.design.theme.AppTheme
import ru.villawonka.app.presentation.design.theme.errorBackgroundEnabled
import ru.villawonka.app.presentation.design.theme.errorContentInverted
import ru.villawonka.app.presentation.design.theme.errorOutlineEnabled
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_plus_24

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun AppTextField(
    modifier: Modifier = Modifier,
    value: String = "",
    label: String? = null,
    placeholder: String? = null,
    supportingText: String? = null,
    isError: Boolean = false,
    leadingIcon: DrawableResource? = null,
    trailingIcon: DrawableResource? = null,
    onValueChange: (String) -> Unit,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    onTextLayout: (TextLayoutResult) -> Unit = {},
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
) {
    val colors = with(MaterialTheme.colors) {
        TextFieldDefaults.outlinedTextFieldColors(
            textColor = if (isError) {
                errorContentInverted
            } else if (enabled) {
                filledTextFieldContentEnabled
            } else {
                filledTextFieldContentDisabled
            },
            placeholderColor = if (isError) {
                errorContentInverted
            } else {
                filledTextFieldContentDisabled
            },
            focusedBorderColor = filledTextFieldOutlineFocused,
            unfocusedBorderColor = filledTextFieldOutlineEnabled,
            backgroundColor = if (isError) {
                errorBackgroundEnabled
            } else {
                filledTextFieldBackgroundEnabled
            },
            errorBorderColor = errorOutlineEnabled,
            cursorColor = filledTextFieldContentFocused
        )
    }

    Column(
        modifier = modifier
    ) {
        label?.let {
            Text(
                text = it,
                style = AppTextStyles.labelLargeMedium,
                color = if (isError) {
                    MaterialTheme.colors.errorContentInverted
                } else {
                    MaterialTheme.colors.filledTextFieldContentEnabled
                }
            )
            AppSpacer(8.dp)
        }

        BasicTextField(
            value = value,
            modifier = Modifier.fillMaxWidth(),
            onValueChange = onValueChange,
            enabled = enabled,
            readOnly = readOnly,
            textStyle = AppTextStyles.labelMediumRegular.copy(
                color = colors.textColor(enabled).value
            ),
            keyboardOptions = keyboardOptions,
            keyboardActions = keyboardActions,
            singleLine = singleLine,
            maxLines = maxLines,
            minLines = minLines,
            visualTransformation = visualTransformation,
            onTextLayout = onTextLayout,
            interactionSource = interactionSource,
            cursorBrush = SolidColor(MaterialTheme.colors.filledTextFieldContentFocused),
            decorationBox = @Composable { innerTextField ->
                TextFieldDefaults.OutlinedTextFieldDecorationBox(
                    value = value,
                    innerTextField = innerTextField,
                    enabled = enabled,
                    singleLine = singleLine,
                    visualTransformation = visualTransformation,
                    interactionSource = interactionSource,
                    isError = isError,
                    label = null,
                    placeholder = {
                        placeholder?.let {
                            Text(
                                text = it,
                                style = AppTextStyles.labelMediumRegular,
                            )
                        }
                    },
                    leadingIcon = leadingIcon?.let {
                        {
                            Icon(
                                painter = painterResource(it),
                                contentDescription = null,
                                tint = if (isError) {
                                    MaterialTheme.colors.errorContentInverted
                                } else {
                                    MaterialTheme.colors.filledTextFieldContentDisabled
                                }
                            )
                        }
                   },
                    trailingIcon = trailingIcon?.let {
                        {
                            Icon(
                                painter = painterResource(it),
                                contentDescription = null,
                                tint = if (isError) {
                                    MaterialTheme.colors.errorContentInverted
                                } else {
                                    MaterialTheme.colors.filledTextFieldContentDisabled
                                }
                            )
                        }
                    },
                    colors = colors,
                    shape = RoundedCornerShape(8.dp),
                    border = {
                        TextFieldDefaults.BorderBox(
                            enabled = enabled,
                            isError = isError,
                            interactionSource = interactionSource,
                            colors = colors,
                            shape = RoundedCornerShape(8.dp),
                            focusedBorderThickness = 1.dp,
                            unfocusedBorderThickness = 1.dp
                        )
                    }
                )
            }
        )

        supportingText?.let {
            AppSpacer(8.dp)
            Text(
                modifier = Modifier.padding(start = 12.dp),
                text = it,
                style = AppTextStyles.labelSmallRegular,
                color = if (isError) {
                    MaterialTheme.colors.errorContentInverted
                } else {
                    MaterialTheme.colors.filledTextFieldContentEnabled
                }
            )
        }
    }
}

val Colors.filledTextFieldContentEnabled
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledTextFieldContentDisabled
    get() = if (isLight) AppColors.grayLight400 else AppColors.grayDark500

val Colors.filledTextFieldContentFocused
    get() = if (isLight) AppColors.grayLight950 else AppColors.grayDark50

val Colors.filledTextFieldBackgroundEnabled
    get() = if (isLight) AppColors.grayLight100 else AppColors.grayDark950
val Colors.filledTextFieldOutlineEnabled
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark700

val Colors.filledTextFieldOutlineFocused
    get() = if (isLight) AppColors.grayLight300 else AppColors.grayDark700


@Preview
@Composable
private fun Preview() {
    Column {
        Column(
            modifier = Modifier
                .background(Color.White)
                .padding(16.dp)
        ) {
            AppTheme(isDarkTheme = false) {
                AppTextField(
                    value = "Enabled",
                    label = "Label",
                    isError = false,
                    trailingIcon = Res.drawable.ic_plus_24,
                    onValueChange = {}
                )
                AppSpacer(24.dp)
                AppTextField(
                    value = "Disabled",
                    label = "Label",
                    enabled = false,
                    trailingIcon = Res.drawable.ic_plus_24,
                    onValueChange = {}
                )
                AppSpacer(24.dp)
                AppTextField(
                    value = "",
                    placeholder = "Placeholder",
                    label = "Label",
                    supportingText = "Supporting text",
                    isError = true,
                    trailingIcon = Res.drawable.ic_plus_24,
                    onValueChange = {}
                )
            }
        }
        Column(
            modifier = Modifier
                .background(Color.Black)
                .padding(16.dp)
        ) {
            AppTheme(isDarkTheme = true) {
                AppTextField(
                    value = "Enabled",
                    label = "Label",
                    isError = false,
                    trailingIcon = Res.drawable.ic_plus_24,
                    onValueChange = {}
                )
                AppSpacer(24.dp)
                AppTextField(
                    value = "Disabled",
                    label = "Label",
                    enabled = false,
                    trailingIcon = Res.drawable.ic_plus_24,
                    onValueChange = {}
                )
                AppSpacer(24.dp)
                AppTextField(
                    value = "",
                    placeholder = "Placeholder",
                    label = "Label",
                    supportingText = "Supporting text",
                    isError = true,
                    trailingIcon = Res.drawable.ic_plus_24,
                    onValueChange = {}
                )
            }
        }
    }
}
